import React, { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  RedirectToSignIn,
  useAuth
} from '@clerk/clerk-react';

import Home           from './pages/Home';
import Blog           from './pages/Blog';
import BlogPost       from './pages/BlogPost';
import Contact        from './pages/Contact';
import Docs           from './pages/Docs';
import Privacy        from './pages/Privacy';
import Terms          from './pages/Terms';
import Dashboard      from './pages/Dashboard';
import SignupChoice   from './pages/SignupChoice';
import Profile        from './pages/Profile';
import Members        from './pages/Members';
import AcceptInvite   from './pages/AcceptInvite';
import RedactionRules from './pages/RedactionRules';
import Settings       from './pages/Settings';

import Pricing        from './pages/Pricing';
import Onboarding     from './pages/Onboarding';
import Billing        from './pages/Billing';
import BillingError   from './pages/BillingError';
import BillingPortal  from './pages/BillingPortal';
import Goodbye        from './pages/Goodbye';

import SignInGate     from './pages/SignInGate';
import AppShell       from './components/AppShell';

import Header         from './components/Header';
import Footer         from './components/Footer';
import ScrollToTop    from './components/ScrollToTop';

import './App.css';

function RequireSelectedPlan({ children }) {
  const location = useLocation();
  try {
    const raw = localStorage.getItem('selectedPlan');
    const plan = raw ? JSON.parse(raw) : null;
    const hasPlan = !!(plan && plan.tier && plan.priceId);
    if (!hasPlan) {
      const dest = encodeURIComponent(location.pathname + location.search);
      return <Navigate to={`/pricing?next=${dest}`} replace />;
    }
  } catch {
    const dest = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/pricing?next=${dest}`} replace />;
  }
  return children;
}

function App() {
  const { isSignedIn } = useAuth();
  const location = useLocation();
  const navigate  = useNavigate();

  useEffect(() => {
    if (isSignedIn && ['/sign-in', '/sign-up'].includes(location.pathname)) {
      navigate('/sign-in-gate');
    }
  }, [isSignedIn, location.pathname, navigate]);

  return (
    <>
      <ScrollToTop />
      <div className="app-layout">
        <Header />

        <main className="main-content">
          <Routes>
            <Route path="/"        element={<Home />} />
            <Route path="/home"    element={<Home />} />
            <Route path="/docs"    element={<Docs />} />
            <Route path="/blog"    element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms"   element={<Terms />} />

            <Route path="/pricing" element={<Pricing />} />

            <Route
              path="/signup-choice/*"
              element={
                <RequireSelectedPlan>
                  <SignupChoice />
                </RequireSelectedPlan>
              }
            />

            <Route
              path="/onboarding"
              element={
                <>
                  <SignedIn><Onboarding /></SignedIn>
                  <SignedOut><RedirectToSignIn redirectUrl="/onboarding" /></SignedOut>
                </>
              }
            />

            <Route
              path="/billing"
              element={
                <>
                  <SignedIn><Billing /></SignedIn>
                  <SignedOut><RedirectToSignIn redirectUrl="/billing" /></SignedOut>
                </>
              }
            />
            <Route path="/billing/success" element={<Navigate to="/iqledger/dashboard" replace />} />
            <Route path="/billing/cancel"  element={<Navigate to="/billing" replace />} />
            <Route path="/billing/error"   element={<BillingError />} />
            <Route
              path="/billing/change-plan"
              element={
                <>
                  <SignedIn><BillingPortal /></SignedIn>
                  <SignedOut><RedirectToSignIn redirectUrl="/billing/change-plan" /></SignedOut>
                </>
              }
            />

            <Route path="/goodbye" element={<Goodbye />} />

            {/* ADD: Gate as a standalone page so afterSignIn → /sign-in-gate actually runs the checkout logic */}
            <Route
              path="/sign-in-gate"
              element={
                <>
                  <SignedIn><SignInGate /></SignedIn>
                  <SignedOut><RedirectToSignIn redirectUrl="/sign-in-gate" /></SignedOut>
                </>
              }
            />

            <Route
              path="/iqledger"
              element={
                <>
                  <SignedIn>
                    <SignInGate>
                      <AppShell />
                    </SignInGate>
                  </SignedIn>
                  <SignedOut><RedirectToSignIn redirectUrl="/iqledger" /></SignedOut>
                </>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile" element={<Profile />} />
              <Route path="members" element={<Members />} />
              <Route path="redaction-rules" element={<RedactionRules />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="/app/*"           element={<Navigate to="/iqledger" replace />} />
            <Route path="/dashboard"       element={<Navigate to="/iqledger/dashboard" replace />} />
            <Route path="/profile"         element={<Navigate to="/iqledger/profile" replace />} />
            <Route path="/members"         element={<Navigate to="/iqledger/members" replace />} />
            <Route path="/redaction-rules" element={<Navigate to="/iqledger/redaction-rules" replace />} />

            <Route path="/invites/accept" element={<AcceptInvite />} />

            <Route
              path="/sign-in/*"
              element={
                <SignIn
                  routing="path"
                  path="/sign-in"
                  signUpUrl="/pricing"
                  afterSignIn={() => window.location.replace('/sign-in-gate')}
                />
              }
            />
            <Route path="/sign-up/*" element={<Navigate to="/pricing" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default App;
